import React from 'react';
import { Linkedin } from 'lucide-react';
import pluckrLogo from '../../assets/images/pluckr-logo.png';

const footerLinks = {
  product: [
    { label: 'AI MCP Servers', href: '#aimcpservers' },
    { label: 'Pricing', href: '#pricing' },
  ],
  legal: [
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
  ],
};

// Custom SVG icon components
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </svg>
);

const socialLinks = [
  { icon: InstagramIcon, href: 'https://www.instagram.com/pluckr.ai?igsh=bWJ3djZ0a2lyNWtx', label: 'Instagram' },
  { icon: Linkedin, href: 'https://www.linkedin.com/in/pluckr', label: 'LinkedIn' },
  { icon: XIcon, href: 'https://x.com/PluckrAI', label: 'X' },
  { icon: DiscordIcon, href: 'https://discord.gg/w9huCAVm', label: 'Discord' },
];

export const Footer = () => {
  return (
    <footer className="bg-[#1a1a1a] border-t border-[#2a2a2a] py-12 md:py-20" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-20">
        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 mb-12 md:mb-16">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <a href="/" className="flex items-center gap-2 mb-6">
              <img src={pluckrLogo} alt="Pluckr" className="h-6 md:h-8 w-auto" />
              <span className="text-xl md:text-2xl font-bold text-[#9be38f]">Pluckr</span>
            </a>

            {/* Social links */}
            <div className="flex gap-3 md:gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[#1a1a1a] flex items-center justify-center text-[#a0a0a0] hover:text-[#9be38f] hover:bg-[#1a1a1a]/80 transition-colors"
                    aria-label={social.label}
                    data-testid={`social-${social.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Product column */}
          <div>
            <h4 className="text-xs md:text-sm font-semibold text-[#a0a0a0] uppercase tracking-wider mb-4 md:mb-6">
              Product
            </h4>
            <ul className="space-y-3 md:space-y-4">
              {footerLinks.product.map((link, index) => (
                <li key={index}>
                  <a href={link.href} className="footer-link text-sm md:text-lg text-[#a0a0a0] hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal column */}
          <div className="col-span-1">
            <h4 className="text-xs md:text-sm font-semibold text-[#a0a0a0] uppercase tracking-wider mb-4 md:mb-6">
              Legal
            </h4>
            <ul className="space-y-3 md:space-y-4">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <a href={link.href} className="footer-link text-sm md:text-lg text-[#a0a0a0] hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 md:pt-8 border-t border-[#2a2a2a] flex flex-col sm:flex-row justify-between items-center gap-3 md:gap-4">
          <p className="text-xs md:text-sm text-[#666666] text-center sm:text-left">
            © 2025 Pluckr. All rights reserved.
          </p>
          <p className="text-xs md:text-sm text-[#666666] text-center">
            For any queries please reach out to us at <a href="mailto:support@pluckr.ai" className="hover:text-[#9be38f] transition-colors">support@pluckr.ai</a>
          </p>
          <p className="text-xs md:text-sm text-[#666666] text-center sm:text-right">
            Made for builders who ship
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
